import React, { useMemo } from "react";
import { heroArtworkState } from "@/lib/journeyGroups";

/**
 * CUST-EXP-001A3A · Hero artwork with a single canonical status pill.
 * -----------------------------------------------------------------
 * The three supplied images are premium illustrative artwork — they
 * are NOT technical drawings, system designs, or the customer's
 * exact property. The only text we render inside the artwork is a
 * compact canonical status pill:
 *
 *   • pre_install  → "Pre-installation"
 *   • in_progress  → "Installation underway"
 *   • completed    → "Installation complete"
 *
 * The live percentage, current phase, next milestone and completion
 * information continue to live in the hero status card and journey
 * rail — never on top of the illustration.
 */

const ASSET_MAP = {
    pre_install: {
        base:  "hero-preinstall",
        label: "Pre-installation",
        alt:   "Illustration representing the pre-installation stage of the customer journey.",
        dot:   "#22B8CF",
    },
    in_progress: {
        base:  "hero-inprogress",
        label: "Installation underway",
        alt:   "Illustration representing installation underway.",
        dot:   "#22B8CF",
    },
    completed: {
        base:  "hero-system",
        label: "Installation complete",
        alt:   "Illustration representing a completed solar installation.",
        dot:   "#3E9E2E",
    },
};

export default function HomeScene({ project = null, lifecycle = "active" }) {
    const state = useMemo(() => heroArtworkState(project), [project]);
    const meta = ASSET_MAP[state] || ASSET_MAP.pre_install;

    return (
        <figure
            className="relative w-full aspect-[4/3] md:aspect-[16/10] rounded-[24px] overflow-hidden border border-white/20 shadow-[0_24px_60px_-30px_rgba(4,10,25,0.55)] isolate m-0 bg-[#0A1226]"
            data-testid="home-scene"
            data-hero-state={state}
            data-lifecycle={lifecycle}
        >
            <picture>
                <source
                    media="(max-width: 720px)"
                    srcSet={`/customer/${meta.base}-sm.webp`}
                    type="image/webp"
                />
                <source srcSet={`/customer/${meta.base}.webp`} type="image/webp" />
                <img
                    src={`/customer/${meta.base}.jpg`}
                    alt={meta.alt}
                    className="absolute inset-0 w-full h-full object-cover object-center select-none"
                    draggable={false}
                    loading="eager"
                    decoding="async"
                    data-testid="home-scene-image"
                />
            </picture>

            {/* Canonical status pill — same treatment on all three states. */}
            <div
                className="absolute left-4 bottom-4 md:left-6 md:bottom-6 z-20 inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 bg-[#0A1226]/95 border border-white/15 text-white text-xs font-medium shadow-md"
                data-testid="home-scene-status-pill"
            >
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: meta.dot }} />
                {meta.label}
            </div>
        </figure>
    );
}
